import { useCallback, useEffect, useRef, useState } from "react";

import { apiUrl, chatBase } from "@/api/client";
import { createDeltaBatcher, type DeltaBatcher } from "./deltaBatcher";

/**
 * Chat hook that speaks the AG-UI SSE wire emitted by
 * ``/chat/stream``. Replaces the deleted ``streamChat`` helper, which
 * parsed the old ``event: token / event: message / event: end``
 * shape; that wire is gone.
 *
 * The hook is a thin state machine over a single in-flight request:
 *
 *   - POST to ``/api/v1/t/{tenant}/c/{chatbot}/chat/stream`` with the
 *     user message and the optional ``session_id``.
 *   - Parse SSE ``data:`` frames into AG-UI events.
 *   - Append ``TEXT_MESSAGE_CONTENT.delta`` to ``assistantText``.
 *   - Collect ``CUSTOM { name: "a2ui" }`` events' ``value`` into
 *     ``a2uiEnvelopes`` for the renderer.
 *   - Capture identifiers off ``RUN_STARTED`` / ``TEXT_MESSAGE_START``.
 *   - Surface ``RUN_FINISHED`` / ``RUN_ERROR`` as terminal states.
 *
 * Cancellation: each ``sendMessage`` aborts any prior in-flight stream
 * before issuing a new one. The hook also aborts on unmount.
 */

export type AGUIRunStatus = "idle" | "streaming" | "error" | "done";

export interface AGUIChatState {
  runStatus: AGUIRunStatus;
  assistantText: string;
  a2uiEnvelopes: Record<string, unknown>[];
  surfaceId: string | null;
  error: string | null;
  sessionId: string | null;
  messageId: string | null;
  toolCalls: Record<string, unknown>[];
}

export interface UseAGUIChatOptions {
  tenant: string | null;
  chatbot: string | null;
  initialSessionId?: string | null;
  onAction?: (action: unknown) => void;
  /**
   * Embed surface reported to the backend ("app" for the mobile-app
   * webview, "web" otherwise). Stamped on the session at mint time;
   * omitted → the backend defaults to "web".
   */
  clientSource?: "web" | "app";
}

export interface UseAGUIChatResult {
  state: AGUIChatState;
  sendMessage: (text: string) => Promise<void>;
  hydrateSession: (sessionId: string | null) => void;
  reset: () => void;
}

const INITIAL_STATE: AGUIChatState = {
  runStatus: "idle",
  assistantText: "",
  a2uiEnvelopes: [],
  surfaceId: null,
  error: null,
  sessionId: null,
  messageId: null,
  toolCalls: [],
};

export function useAGUIChat(opts: UseAGUIChatOptions): UseAGUIChatResult {
  const { tenant, chatbot, initialSessionId = null, clientSource } = opts;

  const [state, setState] = useState<AGUIChatState>(() => ({
    ...INITIAL_STATE,
    sessionId: initialSessionId,
  }));

  const abortRef = useRef<AbortController | null>(null);
  const sessionIdRef = useRef<string | null>(initialSessionId);

  // Keep the ref in sync so a new ``sendMessage`` always includes the
  // most recent thread id without forcing the caller to re-render.
  useEffect(() => {
    sessionIdRef.current = state.sessionId;
  }, [state.sessionId]);

  // Abort any in-flight stream on unmount.
  useEffect(() => {
    return () => {
      abortRef.current?.abort();
      abortRef.current = null;
    };
  }, []);

  const reset = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    sessionIdRef.current = null;
    setState({ ...INITIAL_STATE });
  }, []);

  const hydrateSession = useCallback((sessionId: string | null) => {
    sessionIdRef.current = sessionId;
    setState((s) => ({
      ...s,
      sessionId,
    }));
  }, []);

  const sendMessage = useCallback(
    async (text: string): Promise<void> => {
      if (!tenant || !chatbot) {
        setState((s) => ({
          ...s,
          runStatus: "error",
          error: "Tenant and chatbot must be selected before sending",
        }));
        return;
      }

      // Cancel any prior in-flight stream before kicking off a new one.
      abortRef.current?.abort();
      const controller = new AbortController();
      abortRef.current = controller;

      // Reset transient turn state but keep the existing session_id so
      // the backend treats this as a continuation, not a new thread.
      setState((s) => ({
        ...INITIAL_STATE,
        sessionId: s.sessionId,
        runStatus: "streaming",
      }));

      const url = apiUrl(`${chatBase(tenant, chatbot)}/chat/stream`);
      try {
        const resp = await fetch(url, {
          method: "POST",
          headers: {
            "content-type": "application/json",
            accept: "text/event-stream",
          },
          credentials: "include",
          signal: controller.signal,
          body: JSON.stringify({
            message: text,
            session_id: sessionIdRef.current,
            ...(clientSource ? { client_source: clientSource } : {}),
          }),
        });
        if (!resp.ok || !resp.body) {
          const detail = await safeReadText(resp);
          throw new Error(`HTTP ${resp.status}: ${detail || resp.statusText}`);
        }

        await consumeAGUIStream(resp.body, setState, controller.signal);
      } catch (err) {
        if (controller.signal.aborted) {
          // Aborts are deliberate (re-send or unmount); no error state.
          return;
        }
        const message = err instanceof Error ? err.message : String(err);
        setState((s) => ({
          ...s,
          runStatus: "error",
          error: message,
        }));
      } finally {
        if (abortRef.current === controller) {
          abortRef.current = null;
        }
      }
    },
    [tenant, chatbot, clientSource],
  );

  return { state, sendMessage, hydrateSession, reset };
}

async function safeReadText(resp: Response): Promise<string> {
  try {
    return await resp.text();
  } catch {
    return "";
  }
}

async function consumeAGUIStream(
  body: ReadableStream<Uint8Array>,
  setState: React.Dispatch<React.SetStateAction<AGUIChatState>>,
  signal: AbortSignal,
): Promise<void> {
  // Decode by hand instead of TextDecoderStream — the lib.dom typings
  // disagree about the underlying byte type of TextDecoderStream's
  // writable side, and the manual loop is just as cheap.
  const reader = body.getReader();
  const decoder = new TextDecoder();
  let buffer = "";
  // One assistantText update per frame instead of one per token.
  const textBatcher = createDeltaBatcher((text) => {
    if (signal.aborted) return;
    setState((s) => ({ ...s, assistantText: s.assistantText + text }));
  });

  try {
    while (true) {
      if (signal.aborted) {
        textBatcher.cancel();
        return;
      }
      const { value, done } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      // SSE frames are separated by a blank line. We parse one frame
      // at a time and leave any trailing partial in ``buffer``.
      const frames = buffer.split("\n\n");
      buffer = frames.pop() ?? "";
      for (const frame of frames) {
        const event = parseSseFrame(frame);
        if (!event) continue;
        applyAGUIEvent(event, setState, textBatcher);
      }
    }
    if (buffer.trim()) {
      const event = parseSseFrame(buffer);
      if (event) applyAGUIEvent(event, setState, textBatcher);
    }
  } finally {
    // Land any buffered text before the run-status fallback below (and
    // before the caller records an error); discard it on abort.
    if (signal.aborted) textBatcher.cancel();
    else textBatcher.flush();
    reader.releaseLock();
    // Stream closed (cleanly, by abort, or by error). If the backend
    // emitted RUN_FINISHED / RUN_ERROR we'll already be in ``done`` /
    // ``error``; otherwise the run is effectively over and the UI
    // shouldn't stay locked in ``streaming``. Without this, a
    // dropped connection between the last AG-UI event and the
    // RUN_FINISHED frame leaves the composer permanently disabled.
    setState((s) =>
      s.runStatus === "streaming" ? { ...s, runStatus: "done" } : s,
    );
  }
}

function parseSseFrame(frame: string): Record<string, unknown> | null {
  // Each SSE frame is a series of ``key: value`` lines. We only care
  // about ``data:`` payloads; the backend emits each AG-UI event as a
  // single JSON object per frame.
  const dataLines: string[] = [];
  for (const line of frame.split(/\r?\n/)) {
    if (line.startsWith(":")) continue; // SSE comment / keep-alive
    if (line.startsWith("data:")) {
      dataLines.push(line.slice(5).trimStart());
    }
  }
  if (dataLines.length === 0) return null;
  try {
    return JSON.parse(dataLines.join("\n")) as Record<string, unknown>;
  } catch {
    return null;
  }
}

export function applyAGUIEvent(
  event: Record<string, unknown>,
  setState: React.Dispatch<React.SetStateAction<AGUIChatState>>,
  textBatcher: DeltaBatcher,
): void {
  const type = typeof event.type === "string" ? event.type : "";

  if (type === "TEXT_MESSAGE_CONTENT") {
    const delta = typeof event.delta === "string" ? event.delta : "";
    textBatcher.push(delta);
    return;
  }

  // Every other event lands after the text that preceded it in the
  // stream (e.g. RUN_FINISHED must not overtake the final words).
  textBatcher.flush();

  if (type === "RUN_STARTED") {
    const threadId = typeof event.threadId === "string" ? event.threadId : null;
    if (threadId) {
      setState((s) => ({ ...s, sessionId: threadId }));
    }
    return;
  }

  if (type === "TEXT_MESSAGE_START") {
    const messageId =
      typeof event.messageId === "string" ? event.messageId : null;
    if (messageId) {
      setState((s) => ({ ...s, messageId }));
    }
    return;
  }

  if (type === "CUSTOM" && event.name === "a2ui") {
    const envelope = event.value as Record<string, unknown> | undefined;
    if (!envelope || typeof envelope !== "object") return;
    setState((s) => {
      // Drop duplicate ``createSurface`` envelopes for a surface id we
      // already saw on this turn. The A2UI renderer's MessageProcessor
      // is not idempotent — a second ``createSurface`` for the same id
      // throws "Surface already exists" and silently leaves the surface
      // stuck on its built-in shimmer placeholder (no cards paint).
      // React 19 strict mode and some upstream/backend retries can
      // double-deliver the same envelope; filter at the entry point so
      // every downstream consumer sees one createSurface per surfaceId.
      const newCreate = (envelope as { createSurface?: { surfaceId?: string } })
        .createSurface;
      if (newCreate?.surfaceId) {
        const seen = s.a2uiEnvelopes.some((prev) => {
          const cs = (prev as { createSurface?: { surfaceId?: string } })
            .createSurface;
          return cs?.surfaceId === newCreate.surfaceId;
        });
        if (seen) return s;
      }
      const nextEnvelopes = [...s.a2uiEnvelopes, envelope];
      const nextSurfaceId = s.surfaceId ?? deriveSurfaceId(envelope);
      return {
        ...s,
        a2uiEnvelopes: nextEnvelopes,
        surfaceId: nextSurfaceId,
      };
    });
    return;
  }

  if (
    type === "TOOL_CALL_START" ||
    type === "TOOL_CALL_ARGS" ||
    type === "TOOL_CALL_END"
  ) {
    setState((s) => ({ ...s, toolCalls: [...s.toolCalls, event] }));
    return;
  }

  if (type === "RUN_FINISHED") {
    setState((s) => ({ ...s, runStatus: "done" }));
    return;
  }

  if (type === "RUN_ERROR") {
    const message =
      typeof event.message === "string"
        ? event.message
        : "Run failed";
    setState((s) => ({ ...s, runStatus: "error", error: message }));
    return;
  }
}

function deriveSurfaceId(envelope: Record<string, unknown>): string | null {
  const create = envelope.createSurface as { surfaceId?: unknown } | undefined;
  if (create && typeof create.surfaceId === "string") return create.surfaceId;
  const update = envelope.updateComponents as
    | { surfaceId?: unknown }
    | undefined;
  if (update && typeof update.surfaceId === "string") return update.surfaceId;
  return null;
}
